import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { FlaskConical } from 'lucide-react';
import type { OnboardingProgress } from '@tailoring-crm/shared';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  useConfirm,
  useToast,
} from '@tailoring-crm/ui';
import { useSampleData } from '../../lib/onboarding/use-onboarding.js';

/**
 * Something to click on before the shop has anything of its own — and, more importantly, one
 * button that takes it all away again.
 *
 * The removal is the promise this card is really making, so it says plainly what will go and
 * how much of it there is. A shop that finds demo customers mixed into its real book months
 * later stops trusting the data, and no feature elsewhere recovers that.
 */
export function SampleDataCard({ progress }: { progress: OnboardingProgress }): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const confirm = useConfirm();
  const { load, remove } = useSampleData();

  const loaded = progress.sample.loaded;
  const busy = load.isPending || remove.isPending;

  async function onRemove(): Promise<void> {
    const ok = await confirm({
      title: t('onboarding.sample.confirmTitle'),
      description: t('onboarding.sample.confirmDescription', {
        count: progress.sample.customers,
      }),
      confirmLabel: t('onboarding.sample.remove'),
    });
    if (!ok) return;
    await remove.mutateAsync();
    toast({ title: t('onboarding.sample.removed') });
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center gap-3">
        <FlaskConical aria-hidden="true" className="size-5 text-muted-foreground" />
        <CardTitle>{t('onboarding.sample.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-sm text-muted-foreground">
          {loaded
            ? t('onboarding.sample.loadedDescription', { count: progress.sample.customers })
            : t('onboarding.sample.description')}
        </p>
        <div>
          {loaded ? (
            <Button variant="outline" disabled={busy} onClick={() => void onRemove()}>
              {t('onboarding.sample.remove')}
            </Button>
          ) : (
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => {
                load.mutate(undefined, {
                  onSuccess: () => toast({ title: t('onboarding.sample.loaded') }),
                });
              }}
            >
              {t('onboarding.sample.load')}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
